import { NewbuildingCarousel } from '@cian/frontend-newbuilding-carousel-component';
import { ActionAfterViewObserver, useBooleanState } from '@cian/react-utils';

import { type FC, useCallback, useState } from 'react';

import { type MainCarouselBanner } from 'shared/types/mainCarousel';

import { Layout } from '../Layout';
import { NoImage } from '../NoImage';
import { type AnimationDuration, SliderProgressBar } from '../SliderProgressBar';
import { firstPaintOptions, mediaSettings } from '../Carousel/config';

import { CarouselMainV2Slide } from './internal/components/CarouselMainV2Slide';

import styles from './CarouselMainV2.css';

type CarouselMainV2Props = {
  banners: MainCarouselBanner[];
  interval: AnimationDuration;
  testId?: string;
  onDotClick: (index: number) => void;
  onSelectApartmentClick: (newbuildingId: number, index: number) => void;
  onView: () => void;
};

export const CarouselMainV2: FC<CarouselMainV2Props> = ({
  banners,
  interval,
  testId,
  onDotClick,
  onSelectApartmentClick,
  onView,
}) => {
  const [currentSlide, setCurrentSlide] = useState(1);
  const { state: paused, setFalse: setNotPaused, setTrue: setPaused } = useBooleanState(false);

  const handleAnimationEnd = useCallback(
    () => setCurrentSlide(prev => (prev < banners.length ? prev + 1 : 1)),
    [banners.length],
  );

  if (!banners.length) {
    return (
      <ActionAfterViewObserver
        onInViewport={({ subscribeState }) => {
          subscribeState.setSubscribeUnavailable();
          onView();
        }}
      >
        {({ elementRef }) => (
          <section className={styles['carousel']} ref={elementRef}>
            <NoImage />
          </section>
        )}
      </ActionAfterViewObserver>
    );
  }

  return (
    <ActionAfterViewObserver
      onInViewport={({ subscribeState }) => {
        subscribeState.setSubscribeUnavailable();
        onView();
      }}
    >
      {({ elementRef }) => (
        <section className={styles['carousel']} data-testid={testId} ref={elementRef}>
          <div className={styles['slides']}>
            <NewbuildingCarousel
              defaultCurrentSlide={currentSlide}
              firstPaintOptions={firstPaintOptions}
              media={mediaSettings}
              slides={banners.map((banner, index) => (
                <CarouselMainV2Slide
                  active={index === currentSlide - 1}
                  banner={banner}
                  index={index}
                  key={banner.id}
                  testId={`main-carousel-slide-${banner.id}`}
                  onSelectApartmentClick={onSelectApartmentClick}
                  onTickerMouseLeave={setNotPaused}
                  onTickerMouseOver={setPaused}
                />
              ))}
              onSlideChange={({ currentSlide: slideIndex }) => setCurrentSlide(slideIndex)}
            />
          </div>

          {banners.length > 1 && (
            <div className={styles['progress-bar']}>
              <Layout>
                <div className={styles['progress-bar__wrapper']}>
                  <SliderProgressBar
                    activeIndex={currentSlide - 1}
                    count={banners.length}
                    duration={interval}
                    paused={paused}
                    onAnimationEnd={handleAnimationEnd}
                    onDotClick={index => {
                      onDotClick(index);
                      setCurrentSlide(index + 1);
                    }}
                    onProgressBarHover={setPaused}
                    onProgressBarLeave={setNotPaused}
                  />
                </div>
              </Layout>
            </div>
          )}
        </section>
      )}
    </ActionAfterViewObserver>
  );
};
